import PropTypes from "prop-types"
import React, { useCallback, useEffect } from "react"
import PaymentMethodDropdownOption from "src/main/Billing/PaymentMethodDropdownOption"

import Form from "src/components/Form"
import AcceptedPaymentMethods from "src/components/SelectPaymentMethod/AcceptedPaymentMethods"
import AddPaymentMethodForm from "src/components/SelectPaymentMethod/AddPaymentMethodForm"
import StripeElements from "src/components/StripeElements"

import {
  cancelPaymentMethodSetup as defaultCancelSetup,
  createPaymentMethod as defaultCreatePaymentMethod,
  initializePaymentMethodSetup as defaultInitializeSetup,
} from "src/api/Account/PaymentMethods"

import useSetupIntent from "src/hooks/payment_method_hooks/use_setup_intent"
import useStripeSetup from "src/hooks/payment_method_hooks/use_stripe_setup"
import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

export const ADD_NEW_PAYMENT_METHOD = "add"

const SelectPaymentMethod = ({
  acceptedPaymentMethods,
  paymentMethods,
  setPaymentMethods,
  paymentMethod,
  setPaymentMethod,
  suppressHeading = false,
  disabled = false,
  trackingProps = null,
  initializeSetup = defaultInitializeSetup,
  cancelSetup = defaultCancelSetup,
  createPaymentMethod = defaultCreatePaymentMethod,
}) => {
  const tracker = useTracker()
  const showToast = useToast()
  const { paymentMethod: newPaymentMethod, error } =
    useStripeSetup(createPaymentMethod)

  const addPaymentSelected = paymentMethod === ADD_NEW_PAYMENT_METHOD
  const { activeSetupIntent } = useSetupIntent(
    addPaymentSelected,
    acceptedPaymentMethods,
    false,
    initializeSetup,
    cancelSetup
  )

  const handleCreatePaymentMethodSuccess = useCallback(
    (paymentMethod) => {
      if (trackingProps) {
        tracker.trackEvent(trackingProps.paymentMethodAdded.eventName, {
          payment_method_type: paymentMethod.type,
          ...trackingProps.paymentMethodAdded.eventProps,
        })
      }

      setPaymentMethod(paymentMethod.stripePaymentMethodId)
      setPaymentMethods((prev) => [...prev, paymentMethod])
    },
    [trackingProps, setPaymentMethods, setPaymentMethod, tracker]
  )

  useEffect(() => {
    if (newPaymentMethod) {
      handleCreatePaymentMethodSuccess(newPaymentMethod)
    }
  }, [newPaymentMethod, handleCreatePaymentMethodSuccess])

  useEffect(() => {
    if (error) {
      showToast(error.message, { type: "error" })
    }
  }, [error, showToast])

  const handleSelectChange = (stripePaymentMethodId) => {
    const paymentMethodSelected = stripePaymentMethodId
    setPaymentMethod(paymentMethodSelected)
    const isExistingPaymentMethod =
      paymentMethodSelected !== ADD_NEW_PAYMENT_METHOD

    if (isExistingPaymentMethod) {
      const paymentMethodObject = paymentMethods.find(
        (method) => method.stripePaymentMethodId === paymentMethodSelected
      )

      if (trackingProps) {
        tracker.trackEvent(trackingProps.paymentMethodSelected.eventName, {
          payment_method_type: paymentMethodObject?.type,
          ...trackingProps.paymentMethodAdded.eventProps,
        })
      }
    }
  }

  return (
    <div className="flex flex-col space-y-6">
      <div className="flex flex-col space-y-2">
        {suppressHeading ? null : (
          <span className="text-base font-semibold">Payment Method</span>
        )}
        <AcceptedPaymentMethods
          acceptedPaymentMethods={acceptedPaymentMethods}
        />
      </div>
      <div>
        <Form.Select.Custom
          name="paymentMethod"
          onChange={handleSelectChange}
          value={paymentMethod}
          disabled={disabled}
        >
          <Form.Select.RichOption value="" disabled>
            Select a payment method
          </Form.Select.RichOption>
          {paymentMethods.map((paymentMethod) => (
            <Form.Select.RichOption
              key={paymentMethod.id}
              value={paymentMethod.stripePaymentMethodId}
              hideCheck
            >
              <PaymentMethodDropdownOption
                paymentMethod={paymentMethod}
                includeExpirationDates
              />
            </Form.Select.RichOption>
          ))}
          <Form.Select.RichOption
            key={ADD_NEW_PAYMENT_METHOD}
            value={ADD_NEW_PAYMENT_METHOD}
            hideCheck
          >
            Add a new payment method
          </Form.Select.RichOption>
        </Form.Select.Custom>
      </div>
      {addPaymentSelected && activeSetupIntent && (
        <StripeElements
          options={{
            clientSecret: activeSetupIntent.clientSecret,
          }}
        >
          <AddPaymentMethodForm
            createPaymentMethod={createPaymentMethod}
            onSuccess={handleCreatePaymentMethodSuccess}
          />
        </StripeElements>
      )}
    </div>
  )
}

SelectPaymentMethod.propTypes = {
  // Setup/create/cancel default to the account-scoped APIs; pass overrides to
  // attach the payment method to a different owner (e.g. a marina's contact).
  initializeSetup: PropTypes.func,
  cancelSetup: PropTypes.func,
  createPaymentMethod: PropTypes.func,
  acceptedPaymentMethods: PropTypes.arrayOf(
    PropTypes.oneOf(["card", "us_bank_account"]).isRequired
  ).isRequired,
  paymentMethods: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      stripePaymentMethodId: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      subtitle: PropTypes.string.isRequired,
    }).isRequired
  ),
  setPaymentMethods: PropTypes.func.isRequired,
  paymentMethod: PropTypes.string.isRequired,
  setPaymentMethod: PropTypes.func.isRequired,
  suppressHeading: PropTypes.bool,
  disabled: PropTypes.bool,
  trackingProps: PropTypes.shape({
    paymentMethodAdded: PropTypes.shape({
      eventName: PropTypes.string.isRequired,
      eventProps: PropTypes.shape({}),
    }).isRequired,
    paymentMethodSelected: PropTypes.shape({
      eventName: PropTypes.string.isRequired,
      eventProps: PropTypes.shape({}),
    }).isRequired,
  }),
}

export default SelectPaymentMethod
